import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { 
  LayoutDashboard, 
  Users, 
  CreditCard,
  LogOut,
  Menu,
  X,
  Shield,
  ChevronDown,
  FileText,
  Settings,
  Megaphone,
  ScrollText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const navItems = [
  { path: "/admin", label: "Overview", icon: LayoutDashboard, exact: true },
  { path: "/admin/partners", label: "Partners", icon: Users },
  { path: "/admin/referrals", label: "Referrals", icon: FileText },
  { path: "/admin/payouts", label: "Payouts", icon: CreditCard },
  { path: "/admin/announcements", label: "Announcements", icon: Megaphone },
  { path: "/admin/settings", label: "Settings", icon: Settings },
  { path: "/admin/audit-log", label: "Audit Log", icon: ScrollText },
];

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Set page title based on current route
  useEffect(() => {
    const pageTitles = {
      "/admin": "Admin Dashboard | GeniusList Partners",
      "/admin/partners": "Manage Partners | GeniusList Admin",
      "/admin/referrals": "Manage Referrals | GeniusList Admin",
      "/admin/payouts": "Manage Payouts | GeniusList Admin",
      "/admin/announcements": "Announcements | GeniusList Admin",
      "/admin/settings": "Program Settings | GeniusList Admin",
      "/admin/audit-log": "Audit Log | GeniusList Admin"
    };
    const baseRoute = location.pathname.split('/').slice(0, 3).join('/');
    document.title = pageTitles[baseRoute] || "Admin | GeniusList Partners";
  }, [location.pathname]);

  useEffect(() => {
    const fetchAdmin = async () => {
      try {
        const response = await axios.get(`${API}/admin/auth/me`, { withCredentials: true });
        setAdmin(response.data);
      } catch (error) {
        toast.error("Please login to access admin area");
        navigate('/admin/login', { replace: true });
      } finally {
        setLoading(false);
      }
    };
    
    fetchAdmin();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/auth/logout`, {}, { withCredentials: true });
      toast.success("Logged out successfully");
      navigate('/admin/login', { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  const NavContent = () => (
    <nav className="space-y-1">
      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive = item.exact 
          ? location.pathname === item.path 
          : location.pathname.startsWith(item.path);
        
        return (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={() => setMobileMenuOpen(false)}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
              isActive
                ? "bg-purple-600 text-white"
                : "text-slate-400 hover:bg-slate-800 hover:text-white"
            }`}
            data-testid={`admin-nav-${item.label.toLowerCase()}`}
          >
            <Icon className="w-5 h-5" />
            <span className="font-medium">{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-900 flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-slate-800 border-r border-slate-700">
        <div className="flex flex-col flex-grow pt-5 pb-4 overflow-y-auto">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 mb-8">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-white">GeniusList</span>
              <span className="block text-xs text-purple-400">Admin Panel</span>
            </div>
          </div>
          
          {/* Navigation */}
          <div className="px-4 flex-grow">
            <NavContent />
          </div>
          
          {/* User Profile */}
          <div className="px-4 py-4 border-t border-slate-700">
            <div className="flex items-center gap-3 px-4 py-3">
              <Avatar className="h-9 w-9 bg-purple-600">
                <AvatarFallback className="bg-purple-600 text-white text-sm">
                  {admin?.name?.charAt(0) || 'A'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {admin?.name || 'Admin'}
                </p>
                <p className="text-xs text-slate-400 truncate">
                  {admin?.role || 'admin'}
                </p>
              </div>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={handleLogout}
                className="text-slate-400 hover:text-white hover:bg-slate-700"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-slate-800 border-b border-slate-700">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold text-white">Admin</span>
          </div>
          
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 text-slate-300">
                  <Avatar className="h-8 w-8 bg-purple-600">
                    <AvatarFallback className="bg-purple-600 text-white text-xs">
                      {admin?.name?.charAt(0) || 'A'}
                    </AvatarFallback>
                  </Avatar>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                <DropdownMenuItem onClick={handleLogout} className="text-slate-300 focus:bg-slate-700 focus:text-white">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-slate-300">
                  {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 bg-slate-800 border-slate-700">
                <div className="flex flex-col h-full">
                  <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-700">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                      <Shield className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <span className="text-lg font-bold text-white">GeniusList</span>
                      <span className="block text-xs text-purple-400">Admin Panel</span>
                    </div>
                  </div>
                  
                  <div className="px-4 py-4 flex-grow">
                    <NavContent />
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 lg:pl-64">
        <div className="pt-16 lg:pt-0 min-h-screen">
          <Outlet context={{ admin }} />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
